import {parse, stringify, parseUrl, stringifyUrl, pick, StringifyOptions} from 'query-string';
import scrollIntoView from 'scroll-into-view';
import {format, isBefore, isPast, isSameDay, parseISO} from 'date-fns';
import fileSaver from 'file-saver';
import {v4 as uuidv4} from 'uuid';
import {EMAIL_REGEXP, PHONE_NUMBER_REGEXP, ZIP_REGEXP} from './constants';
import {Primitive} from './generalTypes';

export const pipe = (...funcs: any[]) => {
    const _pipe = (prevFunc: Function, currFunc: Function) => (...arg: any) => currFunc(prevFunc(...arg));

    return funcs.reduce(_pipe);
};

export const equal = (val: Primitive, other: Primitive): boolean => val === other;

export const compose = (...funcs: Function[]): Function => pipe(...funcs.reverse());

export const pass = (val: Primitive) => val;

export const negate = (val: Primitive) => !val;

export const negateFunc = (func: Function) => pipe(func, negate);

export const isPrimitive = (val: Primitive | Object) => val !== Object(val);

export const isBoolean = (val: Primitive) => equal(typeof val, 'boolean');

export const isFunction = (val: Function) => equal(typeof val, 'function');

export const isNumber = (val: Primitive) => equal(typeof val, 'number');

export const isEmpty = (obj: Object | Primitive): boolean => {
    if (isPrimitive(obj)) {
        return !obj;
    }
    if (Array.isArray(obj)) {
        return !obj.length;
    }

    // @ts-ignore
    return isEmpty(Object.keys(obj));
};

export const partial = (func: Function, ...params: any) => (...args: any) => func(...params, ...args);

export const getItemKeyValue = (key: string) => (obj: any) => obj[key];

export const bytesToMegabytes = (bytes: number) => bytes / 1024 / 1000;

export const getEqual = (value: Primitive, key?: string) => {
    return pipe(key ? getItemKeyValue(key) : pass, partial(equal, value));
};

export const getMatches = (rules: {[key: string]: RegExp}, value: string = ''): {[key in keyof typeof rules]: RegExpMatchArray} => {
    return Object
        .entries(rules)
        .reduce((acc, [key, pattern]) => ({...acc, [key]: value.match(pattern)}), {});
};

export const getFromObjSafe = (obj: any, ...path: any): any => {
    const [currentKey, ...restKeys] = path;
    const nextObj = obj ? obj[currentKey] : undefined;

    return currentKey ? getFromObjSafe(nextObj, ...restKeys) : obj;
};

export const omit = (obj: {}, keys: string[]): any => Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key)));

export const pickByKeys = (obj: {}, keys: string[]): any => Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)));

export const getUnfilledArray = (length = 0) => Array(length).fill(undefined);

export const saveFile = (file: any, name = '') => fileSaver.saveAs(file, name);

export const toCapitalize = (str: string) => str && (str.charAt(0).toUpperCase() + str.slice(1).toLowerCase());

export const isEmail = (value: string | null) => !!value && EMAIL_REGEXP.test(value);

export const isPhone = (value: string | null) => !!value && PHONE_NUMBER_REGEXP.test(value);

export const isZIP = (value: string | null) => !!value && ZIP_REGEXP.test(value);

export const validateEmail = (email: string) => !isEmail(email) ? 'Please enter a valid email' : undefined;

export const validateLogin = (value: string) => {
    return isEmail(value) || Number.isInteger(+value) ? undefined : 'Please enter a valid email or phone number';
};

export const validatePhoneNumber = (value: string) => !isPhone(value) ? 'Please enter a valid phone number' : undefined;

export const validateZIP = (value: string) => !isZIP(value) ? 'Please enter a valid ZIP' : undefined;

export const validateRequired = (value: Primitive) => isEmpty(value) ? 'Required' : undefined;

export const validateFileSize = (value: string | string[], maxSize: number) => {
    const errorMessage = `Your uploaded file is too big. Maximum file size is: ${maxSize}Mb`;

    // @ts-ignore
    const files = [].concat(value).filter((item: File) => item instanceof File);
    const isInvalidSize = files.some(({size}) => bytesToMegabytes(size) > maxSize);

    return isInvalidSize ? errorMessage : undefined;
};

export const validateMinLength = (value: string, minLength: number) => {
    return value && value.length < minLength ? `Must be ${minLength} characters or more` : undefined;
};

export const validateMaxLength = (value: string, maxLength: number) => {
    return value && value.length > maxLength ? `Must be ${maxLength} characters or less` : undefined;
};

export const generateUniqueId = () => uuidv4();

export const parseQuery = (query: string, options = {}) => parse(query, {...options, arrayFormat: 'comma'});

// @ts-ignore
export const pickQuery = (query: string, filter: Array<string> | Function, options: object = {}) => pick(query, filter, options);

export const stringifyQueryParams = ({arrayFormat = 'comma', ...params}: any) => stringify(params, {arrayFormat});

export const parseURL = (url: string) => parseUrl(url);

// @ts-ignore
export const stringifyURL = (data: {url: string, query: object}, options: StringifyOptions = {}) => stringifyUrl({...data}, options);

export const formatPhone = (phone: string) => phone && `${phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}`;

export const parsePhone = (phone: string) => phone && phone.replace(/\D/g, '');

export const formatDate = (date: Date | number, dateFormat: string, options: object = {}) => date && format(date, dateFormat, options);

export const parseISODate = (date: string, options = {}) => date && parseISO(date, options);

export const isDateBefore = (date: number, dateToCompare: number) => isBefore(date, dateToCompare);

export const isDatePast = (date: number) => isPast(date);

export const isDateSame = (firstDate: number, secondDate: number) => isSameDay(firstDate, secondDate);

export const scrollToComponent = (component: HTMLElement, time = 500, settings?: any) => scrollIntoView(component, {time, ...settings});

export const getFormattedErrorMessage = (errors: any) => errors && Object.values(errors).join(', ');

export const toMilliseconds = (seconds = 0) => seconds * 1000;

export const toMoney = (amount: number, decimalPlaces = 2) => {
    const formattedAmount = parseFloat(`${amount}`.trim().replace(/,/g, '.'));

    if (!!amount && isNaN(formattedAmount)) {
        return amount;
    }

    const currAmount = formattedAmount || 0;
    const prefix = currAmount >= 0 ? '' : '-';

    return `${prefix}$${Math.abs(currAmount).toFixed(decimalPlaces)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// @ts-ignore-next-line
export const filterUniqArrayOfObject = objKey => (value, index, array) => equal(index, array.findIndex(obj => equal(obj[objKey], value[objKey])));

export const setPropForSort = (...path: any) => (first: any, second: any) => {
    // Convert undefined || null || NaN to 0
    const [firstVal, secondVal] = [first, second].map(obj => getFromObjSafe(obj, ...path) || 0);

    return firstVal - secondVal;
};

export const linkify = (inputText: string) => {
    const linkRegex = /<a([^>]+)href[\s+]?=[\s+]?['"]([^"']+)['|"]([^>]+)?>([^<]+)<\/a([^>]+)?>/gim;

    const formattedContent = inputText.replace(linkRegex, (whole, maybeProps1, hrefLink, maybeProps2, linkText) => (
        hrefLink
            ? `<a ${maybeProps1 ? maybeProps1.trim() : ''} href="${hrefLink.trim()}" ${maybeProps2 ? maybeProps2.trim() : ''}>${linkText && linkText.trim()}</a>`
            : whole
    ));

    const lonelyLinkPattern = /((^|[^>"])(https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|]($|[^<"]))/gim;
    const websiteAddressPattern = /(^|[^("/>)])(www\.[\S]+(\b|$))($|[^(<")])/gim;
    const emailPattern = /(^|[^:>])(\b[a-zA-Z0-9-_.]+@[a-zA-Z_]+?(\.[a-zA-Z]{2,6})+)($|\b[^<"])/gim;
    const phonePattern = /(^|[^:>])(\(([2-9])(?:\d(?!\1)\d|(?!\1)\d\d)\)\s*[2-9]\d{2}-\d{4})($|\b[^<"])/gim;

    return formattedContent
        .replace(lonelyLinkPattern, '$2<a class="link" href="$1" target="_blank">Visit website</a>$4')
        .replace(websiteAddressPattern, '$1<a class="link" href="https://$2" target="_blank">$2</a>$3')
        .replace(emailPattern, '$1<a class="link" href="mailto:$2">$2</a>$4')
        .replace(phonePattern, '$1<a class="link" href="tel:$2">$2</a>$4');
};

export const slugify = (str: string) => str.toString().trim().toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');

export const calculateFunctionExecutionTime = (fn: Function) => {
    return async (...args: any) => {
        const startTime = performance.now();
        const data = await fn(...args);
        const endTime = performance.now();
        const executionTime = endTime - startTime;

        return {executionTime, data: data ?? null};
    };
};

export const getBase64FromUrl = async (url: string) => {
    try {
        const data = await fetch(url);
        const blob = await data.blob();

        return new Promise(resolve => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => resolve(reader.result);
        });
    } catch (error) {
        return null;
    }
};

export const getTransformFileToArrayBuffer = (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.result instanceof ArrayBuffer) {
                resolve(reader.result);
                return;
            }

            reject(new Error('FileReader result is not an ArrayBuffer'));
        };

        reader.readAsArrayBuffer(file);
    });
};

// @ts-ignore
export const promisifyAsyncFunction = (func, resolve = res => res, reject = () => {}) => (...params) => func(...params).then(resolve).catch(reject);

export const getArrayWithReplacedItem = <T>(array: T[], index: number, value: T): T[] => {
    if (index < 0 || index >= array.length) {
        return array;
    }

    return [...array.slice(0, index), value, ...array.slice(index + 1)];
};

export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    delay: number = 300
): (...args: any[]) => Promise<Awaited<ReturnType<T>>> => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return (...args: any[]): Promise<Awaited<ReturnType<T>>> => {
        if (timeout) {
            clearTimeout(timeout);
        }

        return new Promise<Awaited<ReturnType<T>>>(resolve => {
            const next = () => {
                resolve(Promise.resolve(func(...args)) as Awaited<ReturnType<T>>);
            };

            timeout = setTimeout(next, delay);
        });
    };
};

export const throttle = <T extends (...args: any[]) => any>(
    func: T,
    delay: number = 100
): (...args: Parameters<T>) => void => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<T>): void => {
        const next = () => {
            func(...args);
            timeout = null;
        };

        timeout = timeout ?? setTimeout(next, delay);
    };
};
